/**
 * Campo di risposta libera.
 * - Cresce con il testo: niente scroll interno in cui perdersi.
 * - Bozza persistita per chiave in localStorage a ogni battuta: sopravvive a reload,
 *   app in background e chiusura della pagina da parte del sistema (TEST-MATRIX X-03).
 * La bozza si cancella solo con clearDraft(chiave), quando il server ha confermato.
 */
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'

import { readDraft, writeDraft } from './draft'

export type AnswerFieldProps = {
  label: string
  /** Chiave stabile della bozza, es. "wizard:<card_id>". */
  draftKey: string
  onChange?: (value: string) => void
  /** Righe minime visibili a campo vuoto. */
  minRows?: number
  disabled?: boolean
}

export function AnswerField({ label, draftKey, onChange, minRows = 3, disabled }: AnswerFieldProps) {
  const id = useId()
  const ref = useRef<HTMLTextAreaElement>(null)
  const [value, setValue] = useState(() => readDraft(draftKey))

  // Cambio di card: si carica la bozza della nuova chiave.
  useEffect(() => {
    setValue(readDraft(draftKey))
  }, [draftKey])

  const fit = useCallback(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight + 4}px` // +4: bordo di 2px sopra e sotto
  }, [])

  useLayoutEffect(fit, [value, fit])

  // Anche la scala del testo cambia l'altezza necessaria.
  useEffect(() => {
    const observer = new MutationObserver(fit)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-text-scale'] })
    window.addEventListener('resize', fit)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', fit)
    }
  }, [fit])

  return (
    <div>
      <label htmlFor={id} className="text-soft mb-2 mt-[22px] block text-meta text-ink">
        {label}
      </label>
      <textarea
        ref={ref}
        id={id}
        rows={minRows}
        value={value}
        disabled={disabled}
        onChange={(event) => {
          const next = event.target.value
          setValue(next)
          writeDraft(draftKey, next)
          onChange?.(next)
        }}
        className="motion-colors block w-full resize-none overflow-hidden rounded-field border-2 border-line bg-white px-4 py-3.5 text-body text-ink"
      />
    </div>
  )
}
